import type { ReactNode } from 'react';
import { PaymentMethods } from '@repo/domain-kit/shared';
import { usePdfPreviewContext } from '../pdf-preview-context.tsx';
import { FooterPaymentDetailsPlaceholder, FooterPlaceholder } from '../placeholders';
import type { Document, MerchantDetails } from '../types';
import { FooterDetails } from './internals/footer-details.tsx';
import { PageNumber } from './internals/page-number.tsx';
import { PaymentDetails } from './internals/payment-details.tsx';
import { PaymentLink } from './internals/payment-link.tsx';
import { TermsAndConditions } from './internals/terms-and-conditions.tsx';
import styles from './footer.module.css';

interface FooterProps {
  document: Document;
  merchant: MerchantDetails;
  paymentLink?: string | null;
  selectedPaymentMethods?: PaymentMethods[];
}

export function Footer({
  document,
  merchant,
  paymentLink,
  selectedPaymentMethods = [],
}: FooterProps): ReactNode {
  const { type, variant } = usePdfPreviewContext();

  if (type === 'self-billing-invoice') {
    return null;
  }

  if (variant === 'DE') {
    return (
      <footer className={styles['de-layout']}>
        <FooterPlaceholder>
          <PaymentLink className={styles.paymentLink} paymentLink={paymentLink} />
          <PageNumber />
          <FooterDetails document={document} merchant={merchant} />
        </FooterPlaceholder>
      </footer>
    );
  }

  const showPaymentDetails =
    type === 'invoice' &&
    !paymentLink &&
    (!selectedPaymentMethods.length ||
      selectedPaymentMethods.includes(PaymentMethods.BankTransfer));

  return (
    <footer className={styles.layout}>
      <FooterPlaceholder>
        <FooterPaymentDetailsPlaceholder>
          <PaymentLink paymentLink={paymentLink} />

          {showPaymentDetails ? (
            <PaymentDetails
              beneficiaryName={document.beneficiaryName}
              bic={document.bic}
              iban={document.iban}
              reference={document.reference}
            />
          ) : null}

          {paymentLink || showPaymentDetails ? <div className={styles.divider} /> : null}
        </FooterPaymentDetailsPlaceholder>

        <TermsAndConditions merchant={merchant} termsAndConditions={document.termsAndConditions} />
      </FooterPlaceholder>
    </footer>
  );
}
